import { ReactNode } from "react";

interface OrderProperty {
  name: string;
  value: unknown;
}

const OrderPropertiesRenderer = ({
  properties,
}: {
  properties: OrderProperty[];
}) => {
  if (!properties || properties.length === 0) {
    return (
      <div className="flex h-32 w-full items-center justify-center text-gray-500">
        <p>No properties available for this order.</p>
      </div>
    );
  }

  const isHttpString = (value: unknown): value is string =>
    typeof value === "string" && value.startsWith("http");

  const formatPrimitive = (value: unknown): string => {
    if (value === null || value === undefined) {
      return "N/A";
    }
    if (typeof value === "boolean") {
      return value ? "true" : "false";
    }
    return String(value);
  };

  const renderDynamicValue = (value: unknown, keyPrefix: string): ReactNode => {
    if (isHttpString(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          Link
        </a>
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-800">[]</span>;
      }

      return (
        <div className="space-y-1">
          {value.map((item, idx) => (
            <div
              key={`${keyPrefix}-${idx}`}
              className="flex items-baseline gap-2 text-sm"
            >
              <span className="text-xs text-gray-400">{`[${idx}]`}</span>
              <div className="flex-1">
                {renderDynamicValue(item, `${keyPrefix}-${idx}`)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (value && typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);

      if (entries.length === 0) {
        return <span className="text-gray-800">{`{}`}</span>;
      }

      return (
        <div className="space-y-1">
          {entries.map(([childKey, childValue]) => (
            <div
              key={`${keyPrefix}-${childKey}`}
              className="flex items-baseline gap-2 text-sm"
            >
              <span className="text-xs text-gray-400">{childKey}:</span>
              <div className="flex-1">
                {renderDynamicValue(childValue, `${keyPrefix}-${childKey}`)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-gray-800">{formatPrimitive(value)}</span>;
  };

  return (
    <div className="text-sm leading-relaxed">
      {properties.map((property, index) => {
        const rawValue = property.value;
        let parsedValue: unknown = rawValue;
        let parsedSuccessfully = false;

        if (typeof rawValue === "string") {
          try {
            parsedValue = JSON.parse(rawValue);
            parsedSuccessfully = true;
          } catch (error) {
            console.log(error);
            parsedValue = rawValue;
          }
        }

        const isRenderableObject =
          parsedValue !== null &&
          (Array.isArray(parsedValue) || typeof parsedValue === "object");

        if (isRenderableObject) {
          return (
            <div key={index} className="mb-2">
              <div className="text-gray-500">{property.name}:</div>
              <div className="mt-1 ml-2">
                {renderDynamicValue(parsedValue, `${index}`)}
              </div>
            </div>
          );
        }

        const fallbackValue =
          parsedSuccessfully || typeof rawValue !== "string"
            ? parsedValue
            : rawValue;
        const linkHref =
          typeof fallbackValue === "string" && isHttpString(fallbackValue)
            ? fallbackValue
            : null;
        const displayValue = formatPrimitive(fallbackValue);

        return (
          <div key={index} className="mb-2">
            <span className="text-gray-500">{property.name}: </span>
            {linkHref ? (
              <a
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Link
              </a>
            ) : (
              <span className="text-gray-800">{displayValue || "N/A"}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OrderPropertiesRenderer;
